import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { RequestStatusEnum } from '@shared/types';
import { FavoriteObjectEntity, FavoriteObjectId } from '@features/favorites/types';
import { LS_SIDEBAR_COLLAPSED } from '@shared/constants/storagesKeysConstants';

type CommonState = {
    favorites: {
        schemes: FavoriteObjectId[];
        schemesToDisableActions: FavoriteObjectId[];
        schemesGetStatus: RequestStatusEnum;
        schemesAddStatus: RequestStatusEnum;
        schemesDeleteStatus: RequestStatusEnum;
        schemesGetError: AxiosError | null;
        schemesAddError: AxiosError | null;
        schemesDeleteError: AxiosError | null;

        devices: FavoriteObjectId[];
        devicesGetStatus: RequestStatusEnum;
        devicesAddStatus: RequestStatusEnum;
        devicesDeleteStatus: RequestStatusEnum;
        devicesGetError: AxiosError | null;
        devicesAddError: AxiosError | null;
        devicesDeleteError: AxiosError | null;
    };
    isSidebarCollapsed: boolean;
    isSidebarCollapseBlocked: boolean;
};

const initialState: CommonState = {
    favorites: {
        schemes: [],
        schemesToDisableActions: [],
        schemesGetStatus: RequestStatusEnum.NotRequested,
        schemesAddStatus: RequestStatusEnum.NotRequested,
        schemesDeleteStatus: RequestStatusEnum.NotRequested,
        schemesGetError: null,
        schemesAddError: null,
        schemesDeleteError: null,

        devices: [],
        devicesGetStatus: RequestStatusEnum.NotRequested,
        devicesAddStatus: RequestStatusEnum.NotRequested,
        devicesDeleteStatus: RequestStatusEnum.NotRequested,
        devicesGetError: null,
        devicesAddError: null,
        devicesDeleteError: null,
    },
    isSidebarCollapsed: Boolean(localStorage.getItem(LS_SIDEBAR_COLLAPSED)),
    isSidebarCollapseBlocked: false,
};

const commonSlice = createSlice({
    name: '@common',
    initialState,
    reducers: {
        getFavoritesRequest(state, action: PayloadAction<{ entity: FavoriteObjectEntity }>) {
            const { entity } = action.payload;
            state.favorites[`${entity}GetStatus`] = RequestStatusEnum.Loading;
            state.favorites[`${entity}GetError`] = null;
        },
        getFavoritesSuccess(state, action: PayloadAction<{ entity: FavoriteObjectEntity; data: FavoriteObjectId[] }>) {
            const { entity, data } = action.payload;
            state.favorites[`${entity}GetStatus`] = RequestStatusEnum.Success;
            state.favorites[`${entity}GetError`] = null;

            const tmpData = Array.from(new Set(data)); // tmp while bug in backend

            state.favorites[entity] = tmpData || [];
        },
        getFavoritesError(state, action: PayloadAction<{ entity: FavoriteObjectEntity; error: AxiosError }>) {
            const { entity, error } = action.payload;

            state.favorites[`${entity}GetStatus`] = RequestStatusEnum.Error;
            state.favorites[`${entity}GetError`] = error;
        },
        getFavoritesReset(state, action: PayloadAction<{ entity: FavoriteObjectEntity }>) {
            const { entity } = action.payload;
            state.favorites[`${entity}GetStatus`] = RequestStatusEnum.NotRequested;
            state.favorites[`${entity}GetError`] = null;
        },

        addFavoriteRequest(state, action: PayloadAction<{ entity: FavoriteObjectEntity; id: FavoriteObjectId }>) {
            const { entity } = action.payload;
            state.favorites[`${entity}AddStatus`] = RequestStatusEnum.Loading;
            state.favorites[`${entity}AddError`] = null;
        },
        addFavoriteSuccess(state, action: PayloadAction<{ entity: FavoriteObjectEntity; id: FavoriteObjectId }>) {
            const { entity, id } = action.payload;
            state.favorites[`${entity}AddStatus`] = RequestStatusEnum.Success;
            state.favorites[`${entity}AddError`] = null;
            state.favorites[entity].push(id);
        },
        addFavoriteError(state, action: PayloadAction<{ entity: FavoriteObjectEntity; error: AxiosError }>) {
            const { entity, error } = action.payload;
            state.favorites[`${entity}AddStatus`] = RequestStatusEnum.Error;
            state.favorites[`${entity}AddError`] = error;
        },
        addFavoriteReset(state, action: PayloadAction<{ entity: FavoriteObjectEntity }>) {
            const { entity } = action.payload;
            state.favorites[`${entity}AddStatus`] = RequestStatusEnum.NotRequested;
            state.favorites[`${entity}AddError`] = null;
        },

        deleteFavoriteRequest(state, action: PayloadAction<{ entity: FavoriteObjectEntity; id: FavoriteObjectId }>) {
            const { entity } = action.payload;
            state.favorites[`${entity}DeleteStatus`] = RequestStatusEnum.Loading;
            state.favorites[`${entity}DeleteError`] = null;
        },
        deleteFavoriteSuccess(state, action: PayloadAction<{ entity: FavoriteObjectEntity; id: FavoriteObjectId }>) {
            const { entity, id } = action.payload;
            state.favorites[`${entity}DeleteStatus`] = RequestStatusEnum.Success;
            state.favorites[`${entity}DeleteError`] = null;
            state.favorites[entity] = state.favorites[entity].filter((favoriteId) => favoriteId !== id);
        },
        deleteFavoriteError(state, action: PayloadAction<{ entity: FavoriteObjectEntity; error: AxiosError }>) {
            const { entity, error } = action.payload;
            state.favorites[`${entity}DeleteStatus`] = RequestStatusEnum.Error;
            state.favorites[`${entity}DeleteError`] = error;
        },
        deleteFavoriteReset(state, action: PayloadAction<{ entity: FavoriteObjectEntity }>) {
            const { entity } = action.payload;
            state.favorites[`${entity}DeleteStatus`] = RequestStatusEnum.NotRequested;
            state.favorites[`${entity}DeleteError`] = null;
        },
        toggleSidebarCollapsed(state) {
            if (state.isSidebarCollapsed) {
                state.isSidebarCollapsed = false;
                localStorage.removeItem(LS_SIDEBAR_COLLAPSED);
            } else {
                state.isSidebarCollapsed = true;
                localStorage.setItem(LS_SIDEBAR_COLLAPSED, 'true');
            }
        },
        blockSidebar(state) {
            state.isSidebarCollapseBlocked = true;
        },
        unblockSidebar(state) {
            state.isSidebarCollapseBlocked = false;
        },
    },
});

export default commonSlice.reducer;

export const {
    getFavoritesRequest,
    getFavoritesSuccess,
    getFavoritesError,
    getFavoritesReset,
    addFavoriteRequest,
    addFavoriteSuccess,
    addFavoriteError,
    addFavoriteReset,
    deleteFavoriteRequest,
    deleteFavoriteSuccess,
    deleteFavoriteError,
    deleteFavoriteReset,
    toggleSidebarCollapsed,
    blockSidebar,
    unblockSidebar,
} = commonSlice.actions;
